import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getData, getPaged, pagedQs } from '../api/shared';
import { shopifyKeys } from '../queryKeys';
import type { ShopifyCampaignCoupon, ShopifyCouponTerms } from './useCampaignShopify';

export type ShopifyCouponSection =
  | 'active'
  | 'campaign_wide_leftover'
  | 'replaced'
  | 'inactive'
  | 'renamed';

export type ShopifyCouponLiveFilter = 'all' | 'live' | 'not_live';

export type ShopifyCouponSortKey = 'created_at' | 'code' | 'discount_pct';

/** Query of GET /campaigns/{id}/shopify/coupons — one section, one page. */
export interface ShopifyCouponListParams {
  section: ShopifyCouponSection;
  page: number;
  limit: number;
  search?: string;
  live?: ShopifyCouponLiveFilter;
  sort_by?: ShopifyCouponSortKey;
  sort_dir?: 'asc' | 'desc';
}

/** GET /campaigns/{id}/shopify/coupons/summary — always over every visible
 *  coupon, never a page or a search. */
export interface ShopifyCouponSummary {
  total: number;
  /** `renamed` is omitted when zero. */
  counts: Record<Exclude<ShopifyCouponSection, 'renamed'>, number> & {
    renamed?: number;
    /** Visible codes that redeem right now: live assigned plus live replaced,
     *  exhausted excluded. The header reads this and nothing else. */
    live: number;
    /** Codes Deactivate all codes acts on that still work: assigned, plus
     *  replaced codes not yet expired in Shopify, exhausted excluded. Hidden
     *  creators' codes included. */
    on: number;
    /** The replaced part of `on`. */
    replaced_on: number;
    /** The part of `on` that belongs to creators hidden from the caller. */
    hidden_on: number;
    /** Visible replaced codes Shopify still accepts (not expired, limit not reached). */
    replaced_live: number;
  };
  sales_kept: number;
  shop_domain: string;
  primary_campaign_coupon: ShopifyCampaignCoupon | null;
  live_campaign_codes: { id: string; code: string }[];
}

const couponsUrl = (campaignId: string) =>
  `/campaigns/${encodeURIComponent(campaignId)}/shopify/coupons`;

/** One page of one coupon section. The previous page stays visible while the
 *  next loads; `isPlaceholderData` says when that is what is on screen. */
export function useCampaignShopifyCouponsPage(
  campaignId: string,
  params: ShopifyCouponListParams,
  enabled = true,
) {
  const { live, ...rest } = params;
  return useQuery({
    queryKey: shopifyKeys.campaignCoupons(campaignId, params),
    queryFn: () =>
      getPaged<ShopifyCampaignCoupon>(
        `${couponsUrl(campaignId)}?${pagedQs({ ...rest, live: live === 'all' ? undefined : live })}`,
      ),
    enabled: enabled && !!campaignId,
    placeholderData: keepPreviousData,
  });
}

/** Section sizes, totals and the campaign-level coupons the page needs
 *  regardless of which page of which table is showing. */
export function useCampaignShopifyCouponsSummary(campaignId: string, enabled = true) {
  return useQuery({
    queryKey: shopifyKeys.campaignCouponsSummary(campaignId),
    queryFn: () => getData<ShopifyCouponSummary>(`${couponsUrl(campaignId)}/summary`),
    enabled: enabled && !!campaignId,
  });
}
